import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { DeleteIpdMedicalCertificate, GetIpdMedicalCertificates } from "../../../../api/ipd/ipdMedicalCertificateAPI";

const ListIpdMedicalCertificateEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchOpds = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetIpdMedicalCertificates(); // Your API should return a list of Ipd Medical Certificate entries
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          docNo: item.docNo || "",
          type: item.type || "",
          ipdNo: item?.ipdEntryId?.ipdNo || "",
          patientName:
            (item?.ipdEntryId?.patientId?.patientNamePrefix
              ? item?.ipdEntryId?.patientId?.patientNamePrefix + " "
              : "") +
            (item?.ipdEntryId?.patientId?.patientName || ""),
          sex: item?.ipdEntryId?.patientId?.sex || "",
          age: item?.ipdEntryId?.patientId?.age || "",
          mobileNo: item?.ipdEntryId?.patientId?.mobileNo || "",
          address: item?.ipdEntryId?.patientId?.address || "",
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchOpds();
  }, []);

  useEffect(
    () => {
      const results = data.filter(item =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
      setFilteredData(results);
    },
    [search, data]
  );

  const handleEditClick = id => {
    navigate(`/ipd/transaction/ipd-medical-certificate-entry/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteIpdMedicalCertificate(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "10%" },
    { name: "IPD No", selector: row => row.ipdNo, sortable: true, width: "10%" },
    { name: "Patient Name", selector: row => row.patientName, width: "20%" },
    { name: "Sex", selector: row => row.sex, width: "10%" },
    { name: "Age", selector: row => row.age, width: "10%" },
    { name: "Mobile No", selector: row => row.mobileNo, width: "10%" },
    { name: "Address", selector: row => row.address, width: "20%" },
    {
      name: "Actions",
      cell: row =>
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "20%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "IPD No", key: "ipdNo" },
    { label: "Date", key: "date" },
    { label: "Patient Name", key: "patientName" },
    { label: "Sex", key: "sex" },
    { label: "Age", key: "age" },
    { label: "Mobile No", key: "mobileNo" },
    { label: "Address", key: "address" },
  ];

  const ExportCSV = ({ data }) =>
    <CSVLink data={data} headers={headers} filename="ipd_medical_certificate_entries.csv">
      <button className="btn btn-primary mt-1" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>;

  return (
    <Fragment>
      <Breadcrumb parent="Ipd Medical Certificate Entries" title="List Ipd Medical Certificate Entries" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>List Ipd Medical Certificate Entries</h5>
                  <Link
                    to={`/ipd/transaction/ipd-medical-certificate-entry/add`}
                  >
                    <button className="btn btn-primary" id="create">
                      Create Ipd Medical Certificate Entry
                    </button>
                    <UncontrolledTooltip placement="top" target="create">
                      Create New Ipd Medical Certificate Entry
                    </UncontrolledTooltip>
                  </Link>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by IPD No, Patient Name..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListIpdMedicalCertificateEntry;
